import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import {
  filter,
  mergeMap,
  tap,
  Observable,
  Subject,
  map,
  combineLatest,
  BehaviorSubject,
  take,
  catchError,
  of,
  EMPTY,
  startWith,
  pairwise,
  skip,
  switchMap,
  Subscription,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DprDetailsService } from '@dpr-details/services/dpr-details.service';
import {
  AppFacade,
  OfflineFacade,
  UserDetailsFacade,
} from '@shared/state/facades';
import { SelectedSubproject, NavigationMenuItem } from '@shared/models';
import { DprDetails } from '@shared/models/dpr';
import {
  RefreshType,
  Rights,
  StaticMessage,
  UtcDateTimeOptions,
} from '@shared/enums';
import { Sync } from '@shared/models/grid';
import {
  AfterOfflineSyncService,
  CurrentUserDataService,
  DataRefreshService,
  MergeService,
  RoutingService,
  CryptoService,
  UserSessionChangeService,
  ScreenSizeService,
} from '@shared/services';
import { ReportHistoryModalComponent } from '@dpr/components';
import {
  CopyFromPreviousDprService,
  DprAccumulationService,
} from '@dpr-details/services';
import { SubprojectConfiguration } from '@shared/models/project-setup';
import { CopyDprOptionGridData } from '@dpr-details/models/grid';
import { CopyPreviousDprFilters } from '@dpr-details/models';
import { Constants, getStaticMessage, toUTCDate } from '@shared/utils';
import { CopyFromDprModalComponent } from '@dpr-details/components';
import { DprModalTitlePipe } from '@shared/pipes';
import { AutoSaveableComponent } from '@shared/models/component';
import { ConfirmDialogComponent } from '@shared/components';
import { DownloadDprService, DprReportService } from '@report/services';
import { DprService } from '@dpr/services';
import { canActivateTab } from '@dpr-details/guards';

@UntilDestroy()
@Component({
  selector: 'app-dpr-details-page',
  templateUrl: './dpr-details-page.component.html',
  styleUrls: ['./dpr-details-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprDetailsPageComponent implements OnInit {
  dprDetailId: number | string;
  selectedSubproject: SelectedSubproject;
  loading$ = new Subject<boolean>();
  dprDetails$ = new BehaviorSubject<DprDetails>(null);
  canCopyDpr: boolean;
  isFirstDpr: boolean;
  menuItems: NavigationMenuItem[];
  preselectedItem: string | number;

  isLastDpr$ = this.offlineFacade.query.combinedDprDetailsList$.pipe(
    untilDestroyed(this),
    filter((dprDetailsList) => !!dprDetailsList?.length),
    map(
      (dprDetailsList: DprDetails[]) =>
        // double equal needed, because in offline the number typed ids are also sent as strings (in double quote marks)
        // eslint-disable-next-line eqeqeq
        this.dprDetailId == dprDetailsList[0].id
    )
  );
  isAppOnline$ = this.appFacade.query.isAppOnline$;

  Rights = Rights;
  StaticMessage = StaticMessage;

  private activeAutoSaveableComponent: AutoSaveableComponent;
  private dataLoadSub: Subscription;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private userDetailsFacade: UserDetailsFacade,
    private offlineFacade: OfflineFacade,
    private appFacade: AppFacade,
    private dprDetailsService: DprDetailsService,
    private copyDprService: CopyFromPreviousDprService,
    private dialog: MatDialog,
    private dprReportService: DprReportService,
    private mergeService: MergeService,
    private routingService: RoutingService,
    private location: Location,
    private dataRefreshService: DataRefreshService,
    private cryptoService: CryptoService,
    private dprModalTitlePipe: DprModalTitlePipe,
    private datePipe: DatePipe,
    private currentUserDataService: CurrentUserDataService,
    private afterOfflineSyncService: AfterOfflineSyncService,
    private userSessionChangeService: UserSessionChangeService,
    private downloadDprService: DownloadDprService,
    private dprService: DprService,
    private dprAccumulationService: DprAccumulationService,
    protected screenSizeService: ScreenSizeService
  ) {}

  get dprDetails(): DprDetails {
    return this.dprDetails$.value;
  }

  get subprojectConfiguration(): SubprojectConfiguration {
    return this.selectedSubproject.data?.subprojectConfiguration;
  }

  private get dprDetailsRouteSuffix(): string[] {
    return this.location.path().split('/').splice(5);
  }

  private get freeTextFieldTabs(): NavigationMenuItem[] {
    const menuItems: NavigationMenuItem[] = [];

    this.selectedSubproject.data?.freeTextFields.forEach((freeTextField) => {
      menuItems.push({
        id: `free-text-fields-tab-${freeTextField.id}`,
        label: freeTextField.name,
        icon: 'format-align-left',
        link: `free-text-fields/${freeTextField.id}`,
        hasAccess: freeTextField.isActive,
        params: freeTextField.id.toString(),
        dataTestId: freeTextField.name,
      });
    });

    return menuItems;
  }

  private get tableOfColumnTabs(): NavigationMenuItem[] {
    const menuItems: NavigationMenuItem[] = [];

    this.selectedSubproject.data?.tableOfColumns.forEach((tableOfColumn) => {
      menuItems.push({
        id: `tables-of-columns-tab-${tableOfColumn.id}`,
        label: tableOfColumn.name,
        icon: 'format-list-bulleted-square',
        link: `tables-of-columns/${tableOfColumn.id}`,
        hasAccess: tableOfColumn.isActive,
        params: tableOfColumn.id.toString(),
        dataTestId: tableOfColumn.name,
      });
    });

    return menuItems;
  }

  ngOnInit(): void {
    combineLatest([
      this.userDetailsFacade.query.selectedSubproject$.pipe(
        filter((x) => !!x && !!x.data)
      ),
      this.activatedRoute.params.pipe(map((x) => x.dprDetailId)),
    ])
      .pipe(
        untilDestroyed(this),
        tap(([subproject, dprDetailId]) => {
          this.dprDetailId = dprDetailId;
          const oldSelectedSubproject = this.selectedSubproject;
          this.selectedSubproject = subproject;

          this.setMenuItems();

          this.preselectedItem =
            this.dprDetailsRouteSuffix.length > 2
              ? `${this.dprDetailsRouteSuffix[0]}/${this.dprDetailsRouteSuffix[1]}`
              : this.dprDetailsRouteSuffix[0];

          // verify if user rights changed: if yes, do not refresh the data, to avoid data loss if there are unsaved changes
          if (
            this.userSessionChangeService.isUserSessionChange(
              oldSelectedSubproject,
              subproject
            )
          ) {
            return;
          }

          // load the data only if the subprojectId from the route matches the one from the selected subproject
          // this is needed to avoid 'The subprojectId does not match the dprDetailId' error
          // when the user attempts to access DPR details from a subproject he/she does not have access to
          const subprojectIdFromRoute = this.cryptoService.decryptId(
            this.routingService.subprojectIdFromCurrentRoute
          );
          if (subprojectIdFromRoute === this.selectedSubproject.id) {
            this.getData();
          }
        })
      )
      .subscribe();

    this.offlineFacade
      .getDprDetails(this.dprDetailId)
      .pipe(
        untilDestroyed(this),
        switchMap(() => this.offlineFacade.getDprDetails(this.dprDetailId)),
        tap((dprDetails: DprDetails) => {
          const firstWarning = dprDetails?.firstWarning;
          const warningStatusTab = dprDetails?.warningStatusTab;
          if (dprDetails) {
            this.canCopyDpr = this.copyDprService.getCanCopyDpr(
              dprDetails,
              this.subprojectConfiguration
            );
            this.dprDetails$.next({
              ...dprDetails,
              firstWarning,
              warningStatusTab,
            });
          }
        })
      )
      .subscribe();

    // recompute condition in case isAppOnline changes
    this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        // first computation is done on getData()
        skip(1),
        filter((x) => x),
        tap(() => {
          this.computeIsFirstDpr();
        })
      )
      .subscribe();

    this.registerEvents();
  }

  navigateToDprList(): void {
    this.routingService.navigateToRoute(
      this.routingService.routePrefix,
      'dprs',
      this.cryptoService.encryptId(this.selectedSubproject.id)
    );
  }

  openCopyFromPreviousDprModal(): void {
    const copyDprOptions = this.copyDprService.getCopyFromPreviousDprOptions(
      this.selectedSubproject,
      this.dprDetails
    );

    const copyFromPreviousDprDialog = this.dialog.open(
      CopyFromDprModalComponent,
      {
        ...Constants.DialogOptions,
        disableClose: false,
        data: {
          title: this.dprModalTitlePipe.transform(
            this.dprDetailsService.getModalTitle(
              this.dprDetails.dprDisplayId,
              this.datePipe.transform(
                toUTCDate(
                  new Date(this.dprDetails.dprDateTime),
                  UtcDateTimeOptions.UtcDateWithoutTime
                ),
                'MMM dd'
              ),
              this.dprDetails.revision
            ),
            $localize`:@@dprDetails.dprDetailsPage.txtCopyAllData:Copy all data from previous DPR`
          ),
          gridData: copyDprOptions,
          isAppOnline$: this.isAppOnline$,
        },
      }
    );

    copyFromPreviousDprDialog
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter((batchChanges: Sync<CopyDprOptionGridData>) => !!batchChanges),
        mergeMap((batchChanges: Sync<CopyDprOptionGridData>) =>
          this.confirmUnsavedChanges().pipe(
            // Only proceed if user confirms to continue (or no unsaved changes exist)
            mergeMap(() =>
              this.processCopyOperation(copyDprOptions, batchChanges)
            )
          )
        )
      )
      .subscribe();
  }

  downloadDprReport(): void {
    this.isAppOnline$
      .pipe(
        take(1),
        tap((isAppOnline) => {
          isAppOnline
            ? this.openReportHistoryModal()
            : this.downloadDprService.downloadOffline(
                this.dprDetails.id,
                this.dprDetails.firstWarning != null
              );
        })
      )
      .subscribe();
  }

  openReportHistoryModal(): void {
    this.dprReportService
      .getDprReportHistory(this.dprDetails.dprId)
      .pipe(
        untilDestroyed(this),
        tap((reportHistoryResponse) => {
          this.dialog.open(ReportHistoryModalComponent, {
            ...Constants.DialogOptions,
            disableClose: false,
            data: {
              addWatermark: this.dprDetails.firstWarning != null,
              dprDetailId: this.dprDetails.id,
              displayId: this.dprDetails.dprDisplayId,
              isDigitalSignatureEnabled:
                this.subprojectConfiguration.enableDigitalSignature,
              title: this.dprModalTitlePipe.transform(
                this.dprDetailsService.getModalTitle(
                  this.dprDetails.dprDisplayId,
                  this.datePipe.transform(
                    toUTCDate(
                      new Date(this.dprDetails.dprDateTime),
                      UtcDateTimeOptions.UtcDateWithoutTime
                    ),
                    'MMM dd'
                  ),
                  this.dprDetails.revision
                ),
                $localize`:@@dprDetails.dprDetailsPage.txtDownload:Download`
              ),
              reportHistory: reportHistoryResponse,
            },
          });
        })
      )
      .subscribe();
  }

  goToNextDpr(): void {
    this.dprDetailsService
      .getNextDprDetailsId(this.dprDetails.dprDateTime)
      .pipe(
        filter((x) => !!x),
        tap((nextDprDetailsId) =>
          this.routingService.navigateToRoute(
            this.routingService.routePrefix,
            'dpr-details',
            this.cryptoService.encryptId(this.selectedSubproject.id),
            `${nextDprDetailsId}`,
            ...this.dprDetailsRouteSuffix
          )
        )
      )
      .subscribe();
  }

  goToPreviousDpr(): void {
    this.dprDetailsService
      .getPreviousDprDetailsId(this.dprDetails.dprDateTime)
      .pipe(
        filter((x) => !!x),
        tap((previousDprDetailsId) =>
          this.routingService.navigateToRoute(
            this.routingService.routePrefix,
            'dpr-details',
            this.cryptoService.encryptId(this.selectedSubproject.id),
            `${previousDprDetailsId}`,
            ...this.dprDetailsRouteSuffix
          )
        )
      )
      .subscribe();
  }

  componentActivated(component: Component): void {
    if ((component as AutoSaveableComponent).autoSave) {
      this.activeAutoSaveableComponent = component as AutoSaveableComponent;
    }
  }

  componentDeactivated(): void {
    this.activeAutoSaveableComponent = null;
  }

  onSelectionChange(
    currentSelection: string | number,
    isActive: boolean = true
  ): void {
    if (isActive) {
      this.preselectedItem = currentSelection;
    }
  }

  //#region private methods

  private getData(): void {
    // The page is reused when the user moves to another DPR and reloads on a
    // refresh, so a load still in flight is cancelled: finishing late, it would put
    // an outdated or different DPR on the page and in the offline store.
    this.dataLoadSub?.unsubscribe();
    this.loading$.next(true);
    this.dprAccumulationService.reset();

    const currentDpr$ = this.dprDetailsService
      .getDprDetails(this.dprDetailId)
      .pipe(
        // The carry-over and formula columns are worked out before this DPR reaches
        // the offline store, because that is what makes the tabs build their grids.
        switchMap((dprDetails) =>
          this.dprAccumulationService
            .load(dprDetails, this.selectedSubproject.data)
            .pipe(map(() => dprDetails))
        ),
        tap((dprDetails) => {
          this.offlineFacade.setDprDetails(dprDetails);
          this.dprDetails$.next(dprDetails);

          this.computeIsFirstDpr();
        }),
        catchError(() => {
          this.onError();
          return of(EMPTY);
        }),
        // On completion rather than in finalize, so a load cancelled by the next one
        // leaves the loading state to that one.
        tap({
          complete: () =>
            // timeout needed for the loading of data in offline
            setTimeout(() => {
              this.loading$.next(false);
            }),
        })
      );

    this.dataLoadSub = this.offlineFacade.query.dprDetailsList$
      .pipe(
        take(1),
        switchMap((dprDetailsList) => {
          if (!dprDetailsList) {
            return this.dprService.getLastThreeDprs().pipe(
              tap((dprsList) => {
                this.offlineFacade.setDprDetailsList(dprsList);
              }),
              mergeMap(() => currentDpr$)
            );
          }
          return currentDpr$;
        }),
        // Last, so that leaving the page also cancels the requests in flight.
        untilDestroyed(this)
      )
      .subscribe();
  }

  private registerEvents(): void {
    this.dataRefreshService
      .getRefreshNeededWithData()
      .pipe(
        untilDestroyed(this),
        filter((refreshNeeded) => !!refreshNeeded),
        tap((refreshNeeded) => {
          switch (refreshNeeded.refreshType) {
            case RefreshType.Data:
              this.getData();
              break;
            case RefreshType.ProjectSetupChange:
              if (this.activeAutoSaveableComponent) {
                this.activeAutoSaveableComponent
                  .autoSaveFromAdminChange()
                  .pipe(
                    take(1),
                    tap(() => {
                      this.checkTabAccessRights();
                    })
                  )
                  .subscribe();
              }
              break;
            case RefreshType.UserSessionChange:
              if (refreshNeeded.data?.isSubprojectStillActive === false) {
                this.dialog.open(ConfirmDialogComponent, {
                  ...Constants.DialogOptions,
                  data: {
                    title: $localize`:@@dprDetails.dprDetailsPage.titleSubprojectNotAvailable:Subproject no longer available`,
                    message: $localize`:@@dprDetails.dprDetailsPage.txtSubprojectNotAvailable:The current subproject is no longer active or you do not have the required permissions. Changes may not be saved anymore and another available subproject should be selected.`,
                    buttonLabel: getStaticMessage(StaticMessage.OkButton),
                    hideCancelButton: true,
                  },
                });
              }
              break;
            case RefreshType.AfterOfflineSync:
              // triggered after the offline sync is done
              this.handleRefreshAfterOfflineSync();
              break;
          }

          // never uncomment this block!
          // DPR Details must not be forcefully refreshed, to prevent accidental loss of unsaved data
          // if user rights are withdrawn for any of its tabs/fields, the server will reject the sync with HTTP 403 (forbidden)
          /*$scope.$on(appSettings.broadcast.triggerResetIsDirtyFlagAndReload, function () {
                  $window.onbeforeunload = null;
                  $window.location.reload();
          });*/
        })
      )
      .subscribe();

    this.isAppOnline$
      .pipe(
        untilDestroyed(this),
        startWith(null),
        pairwise(),
        filter(
          ([previousValue, currentValue]) =>
            previousValue !== null && // skip first emission
            previousValue !== currentValue &&
            currentValue === true
        ),
        tap(() => {
          this.activeAutoSaveableComponent.autoSave();
        })
      )
      .subscribe();
  }

  private handleRefreshAfterOfflineSync(): void {
    // if dprDetailId is a GUID generated while offline,
    // after sync we need to reload the dpr details with new dprDetailId generated while syncing the data
    const dprId = Number(this.dprDetails.dprId) ? this.dprDetails.dprId : 0;

    this.dprDetailsService
      .getDprDetailsId(dprId, this.dprDetails.dprDateTime)
      .pipe(
        untilDestroyed(this),
        take(1),
        mergeMap((dprDetailId) => {
          return this.dprDetailsService.getDprDetails(dprDetailId);
        }),
        mergeMap((dprDetails) => {
          // double equal needed, because in offline the ids are sent as strings
          // eslint-disable-next-line eqeqeq
          if (dprDetails.id != this.dprDetailId) {
            this.offlineFacade.updateDprDetails(this.dprDetailId, dprDetails);

            // update the dpr detail id in the route
            this.routingService.navigateToRoute(
              this.routingService.routePrefix,
              'dpr-details',
              this.cryptoService.encryptId(this.selectedSubproject.id),
              `${dprDetails.id}`,
              ...this.dprDetailsRouteSuffix
            );
          }

          this.checkTabAccessRights();

          return Number(this.dprDetailId)
            ? this.userDetailsFacade.query.userDetails$
            : this.currentUserDataService.getUserDetails(
                this.routingService.routePrefix
              );
        }),
        take(1),
        tap((userDetails) => {
          if (userDetails.selectedSubproject) {
            // TODO(Old) verify if another user logs in (comment from the old app)

            // the page reload will be triggered when handling the event
            this.afterOfflineSyncService.processAfterOfflineSync();
          }
        })
      )
      .subscribe();
  }

  private computeIsFirstDpr(): void {
    this.dprDetailsService
      .isFirstDpr(this.dprDetails.dprDateTime)
      .pipe(
        untilDestroyed(this),
        tap((isFirstDpr: boolean) => {
          this.isFirstDpr = isFirstDpr;

          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private copyFromPreviousDpr(
    dprDetailsSectionsFilters: CopyPreviousDprFilters
  ): Observable<DprDetails> {
    return this.dprDetailsService
      .copyDprFromPrevious(dprDetailsSectionsFilters)
      .pipe(
        tap((dprDetails: DprDetails) => {
          this.dprDetails$.next(dprDetails);
          this.offlineFacade.setDprDetails(dprDetails);
          this.canCopyDpr = this.copyDprService.getCanCopyDpr(
            dprDetails,
            this.subprojectConfiguration
          );
          this.dataRefreshService.setRefreshNeeded();
        })
      );
  }

  private checkTabAccessRights(): void {
    const canAccess$ = canActivateTab(
      this.activatedRoute.firstChild.snapshot,
      this.routingService,
      this.userDetailsFacade
    );
    canAccess$.pipe(take(1)).subscribe();
  }

  private setMenuItems(): void {
    this.menuItems = [
      {
        id: 'activities-tab',
        label:
          this.subprojectConfiguration.customNames.activitiesTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblActivities:Activities`,
        icon: 'inbox',
        link: 'activities',
        hasAccess: true,
        dataTestId: 'activities-tab',
      },
      {
        id: 'status-tracker-tab',
        label:
          this.subprojectConfiguration.customNames.statusTrackerTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblStatusTracker:Status Tracker`,
        icon: 'chart-gantt',
        link: 'status-tracker',
        hasAccess: this.subprojectConfiguration.enableStatusTrackerTab,
        dataTestId: 'status-tracker-tab',
      },
      {
        id: 'weather-tab',
        label:
          this.subprojectConfiguration.customNames.weatherTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblWeather:Weather`,
        icon: 'weather-partly-cloudy',
        link: 'weather',
        hasAccess: true,
        dataTestId: 'weather-tab',
      },
      {
        id: 'hse-tab',
        label:
          this.subprojectConfiguration.customNames.hseTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblHSE:HSE`,
        icon: 'lifebuoy',
        link: 'hse',
        hasAccess: true,
        dataTestId: 'hse-tab',
      },
      {
        id: 'personnel-tab',
        label:
          this.subprojectConfiguration.customNames.personnelTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblPersonnel:Personnel`,
        icon: 'account-multiple',
        link: 'personnel',
        hasAccess: true,
        dataTestId: 'personnel-tab',
      },
      {
        id: 'consumables-tab',
        label:
          this.subprojectConfiguration.customNames.consumablesTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblConsumables:Consumables`,
        icon: 'dolly',
        link: 'consumables',
        hasAccess: true,
        dataTestId: 'consumables-tab',
      },
      {
        id: 'summary-tab',
        label:
          this.subprojectConfiguration.customNames.summaryTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblSummary:Summary`,
        icon: 'message-text',
        link: 'summary',
        hasAccess: true,
        dataTestId: 'summary-tab',
      },
      {
        id: 'forecast-tab',
        label:
          this.subprojectConfiguration.customNames.forecastTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblForecast:Forecast`,
        icon: 'binoculars',
        link: 'forecast',
        hasAccess: this.subprojectConfiguration.enableForecast,
        dataTestId: 'forecast-tab',
      },
      {
        id: 'appendix-tab',
        label:
          this.subprojectConfiguration.customNames.appendixTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblAppendix:Appendix`,
        icon: 'file-plus',
        link: 'appendix',
        hasAccess: this.subprojectConfiguration.enableAppendix,
        dataTestId: 'appendix-tab',
      },
      ...this.freeTextFieldTabs,
      {
        id: 'progress-count-with-target-tab',
        label:
          this.subprojectConfiguration.customNames.progressTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblProgressCountWithTarget:Progress Count With Target`,
        icon: 'chart-line',
        link: 'progress-count-with-target',
        hasAccess: this.subprojectConfiguration.enableProgressTab,
        dataTestId: 'progress-count-with-target-tab',
      },
      {
        id: 'progress-count-without-target-tab',
        label:
          this.subprojectConfiguration.customNames
            .progressWithoutTargetTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblProgressCountWithoutTarget:Progress Count w/o Target`,
        icon: 'chart-bar',
        link: 'progress-count-without-target',
        hasAccess: this.subprojectConfiguration.enableProgressWithoutTargetTab,
        dataTestId: 'progress-count-without-target-tab',
      },
      {
        id: 'hidden-free-text-fields-tab',
        label:
          this.subprojectConfiguration.customNames.hiddenTabName ??
          $localize`:@@dprDetails.dprDetailsPage.lblHiddenFreeTextFields:Hidden free text fields`,
        icon: 'eye-off',
        link: 'hidden-free-text-fields',
        hasAccess:
          this.subprojectConfiguration.enableHiddenTab &&
          this.selectedSubproject.canAccessHiddenComments,
        dataTestId: 'hidden-free-text-fields-tab',
      },
      ...this.tableOfColumnTabs,
    ];
  }

  private onError(): void {
    // Redirect to error page if the preselected dpr (from URL) is not available for offline
    this.router.navigate(['/error'], {
      queryParams: {
        errorType: 'dpr-not-loaded',
      },
    });
  }

  private confirmUnsavedChanges(): Observable<boolean> {
    if (!this.hasUnsavedChanges()) {
      return of(true);
    }

    return this.dialog
      .open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          title: getStaticMessage(StaticMessage.UnsavedChangesTitle),
          message: getStaticMessage(
            StaticMessage.UnsavedChangesText,
            StaticMessage.CopyText,
            StaticMessage.CopyOperationText
          ),
          buttonLabel: getStaticMessage(StaticMessage.ContinueButton),
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((confirmed) => !!confirmed)
      );
  }

  private hasUnsavedChanges(): boolean {
    if (!this.activeAutoSaveableComponent) {
      return false;
    }

    const component = this.activeAutoSaveableComponent as any;

    // Check direct isDirty$ on page or nested component's (grid container's) isDirty$
    return (
      component.isDirty$?.value ||
      component.activeAutoSaveableComponent?.isDirty$?.value ||
      false
    );
  }

  private processCopyOperation(
    copyDprOptions: CopyDprOptionGridData[],
    batchChanges: Sync<CopyDprOptionGridData>
  ): Observable<DprDetails> {
    const filters = this.mergeService.applyChanges(
      copyDprOptions,
      batchChanges
    );

    const dprDetailsSectionsFilters =
      this.copyDprService.convertCopyPreviousDprOptionsToFilters(
        this.selectedSubproject.id,
        this.dprDetailId,
        filters
      );

    return this.copyFromPreviousDpr(dprDetailsSectionsFilters);
  }

  //#endregion
}
